/**
 * Amtrend AI Assistant - Chat-First Application
 * Claude-like UI for interacting with Epicor ERP
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from './hooks/useTheme'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { SessionProvider } from './hooks/useSessions'
import { ToastProvider } from './hooks/useToast'
import Login from './pages/Login'
import ChatApp from './pages/ChatApp'
import AdminDashboard from './pages/AdminDashboard'
import Settings from './pages/Settings'
import KnowledgeBase from './pages/KnowledgeBase'
import EntityHubPage from './pages/EntityHubPage'
import ProductionFlow from './pages/ProductionFlow'
import Workspace from './pages/Workspace'
import SystemStatusBanner from './components/SystemStatusBanner'

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-surface-dark">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 dark:border-gray-700 border-t-amtrend-blue"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <SessionProvider>{children}</SessionProvider>
}

// Main App with routes
function AppRoutes() {
  return (
    <>
      {/* System status banner shows when database is down */}
      <SystemStatusBanner />

      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute>
              <KnowledgeBase />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/entity/:entityType/:entityId"
          element={
            <ProtectedRoute>
              <EntityHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production"
          element={
            <ProtectedRoute>
              <ProductionFlow />
            </ProtectedRoute>
          }
        />
        {/* Must be declared BEFORE the catch-all below, which otherwise
            swallows every path into ChatApp. */}
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <Workspace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <ChatApp />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  )
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App
